"use client";
import { formatPrice } from "@/utils/common";
import { getUrlOfPage } from "@/utils/data/page";
import {
  getAllServicePrice,
  getService,
  getServicesWithRegexes,
  Service,
} from "@/utils/data/services";
import { getBg } from "@/utils/style";
import Link from "next/link";
import React from "react";
import { useEffect, useRef } from "react";

export default function TablePriceList() {
  const table = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (!table.current) {
      return;
    }

    const tableElement = table.current;
    const tableContainer = tableElement.parentElement;
    const thead = tableElement.querySelector("thead");
    const theadCells = Array.from(
      tableElement.querySelectorAll("thead > tr > th")
    ) as HTMLTableCellElement[];
    const firstDataRow = tableElement.querySelector(
      "tbody > tr"
    ) as HTMLTableRowElement;

    if (
      !(tableContainer instanceof HTMLDivElement) ||
      !thead ||
      !theadCells.length ||
      !firstDataRow
    ) {
      return;
    }

    const getHeaderHeight = () => {
      return document.querySelector("header > div")?.clientHeight || 0;
    };

    const resetHead = (tableClientRect: DOMRect) => {
      for (let i = 0; i < firstDataRow.children.length; ++i) {
        const cell = firstDataRow.children[i];
        if (cell instanceof HTMLTableCellElement) {
          cell.style.paddingTop = 12 + "px";
        }
      }

      thead.style.position = "static";
      thead.style.left = tableClientRect.left + "px";
      thead.style.width = tableElement.clientWidth + "px";
      theadCells[0].style.borderTopLeftRadius = "";
      theadCells[theadCells.length - 1].style.borderTopRightRadius = "";
    };

    const scrollHead = (tableClientRect: DOMRect) => {
      const cells = Array.from(
        tableElement.querySelectorAll("tbody > tr:first-child > td")
      );

      for (let i = 0; i < firstDataRow.children.length; ++i) {
        const cell = firstDataRow.children[i];
        if (cell instanceof HTMLTableCellElement) {
          cell.style.paddingTop = thead.clientHeight + 12 + "px";
        }
      }

      thead.style.zIndex = "20";
      thead.style.position = "fixed";
      thead.style.top = getHeaderHeight() + "px";
      thead.style.left = tableClientRect.left + "px";
      thead.style.width = tableElement.clientWidth + "px";
      theadCells[0].style.borderTopLeftRadius = "0px";
      theadCells[theadCells.length - 1].style.borderTopRightRadius = "0px";
      cells.forEach((cell, i) => {
        if (theadCells[i]) {
          theadCells[i].style.width = cell.clientWidth + "px";
        }
      });
    };

    const stuckHead = (tableClientRect: DOMRect) => {
      thead.style.top = tableClientRect.bottom - thead.clientHeight + "px";
      thead.style.left = tableClientRect.left + "px";
      thead.style.width = tableElement.clientWidth + "px";
      theadCells[0].style.borderTopLeftRadius = "0px";
      theadCells[theadCells.length - 1].style.borderTopRightRadius = "0px";
    };

    const handleTableChange = () => {
      const tableClientRect = tableElement.getBoundingClientRect();
      const headerHeight = getHeaderHeight();
      if (headerHeight <= tableClientRect.top) {
        resetHead(tableClientRect);
      } else if (headerHeight + thead.clientHeight <= tableClientRect.bottom) {
        scrollHead(tableClientRect);
      } else {
        stuckHead(tableClientRect);
      }
    };

    handleTableChange();
    window.addEventListener("scroll", handleTableChange);
    window.addEventListener("resize", handleTableChange);
    tableContainer.addEventListener("scroll", handleTableChange);

    return () => {
      window.removeEventListener("scroll", handleTableChange);
      window.removeEventListener("resize", handleTableChange);
      tableContainer.addEventListener("scroll", handleTableChange);
    };
  }, [table]);

  /**
   * Replaces the name of the service to a link that redirects to the service's details page
   * @param title
   * @returns
   */
  const formatTitle = (title: string): React.ReactNode => {
    const servicesWithRegexes = getServicesWithRegexes();

    for (const { regex, service } of servicesWithRegexes) {
      const matches = title.match(regex);
      if (matches) {
        const url = getUrlOfPage(service.id);

        if (url) {
          return (
            <>
              {matches[1]}
              <Link href={url}>{matches[2]}</Link>
              {matches[3]}
            </>
          );
        }
      }
    }

    return title;
  };

  const servicesPrice = getAllServicePrice();
  let lastServiceId: string;

  return (
    <div className="table-with-scroll px-2">
      <table ref={table}>
        <caption>
          Behemke szolgáltatás árak - {new Date().getFullYear()}
        </caption>
        <thead>
          <tr>
            <th>Szolgáltatás</th>
            <th>Időtartam</th>
            <th className="min-w-[130px]">Ár</th>
            <th>Megjegyzés</th>
          </tr>
        </thead>
        <tbody>
          {servicesPrice.map(
            (
              {
                serviceId,
                title,
                duration,
                price,
                priceUnit,
                pass,
                passIncludesFreeConsultation,
                comment,
                commentConsultation,
              },
              i
            ) => {
              let service: Service | null = null;
              if (serviceId && lastServiceId !== serviceId) {
                service = getService(serviceId);
                lastServiceId = serviceId;
              }

              return (
                <React.Fragment key={title}>
                  {/* Service devider row */}
                  {service && (
                    <tr className="relative">
                      <td colSpan={5} className="pt-6 pb-0 bg-transparent">
                        <span
                          id={service.anchorId}
                          className="absolute -top-20"
                        ></span>
                        <span
                          className={`block p-2 rounded-full ${getBg(
                            service.color
                          )}`}
                        >
                          {service.title}

                          {service.expectedStart && (
                            <span className="ml-2 text-sm">
                              (várható indulás: {service.expectedStart})
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  )}

                  {/* Price of the service */}
                  <tr
                    className={
                      i !== 0 && !service ? "border-t-1 border-t-gray-300" : ""
                    }
                  >
                    <td>{formatTitle(title)}</td>
                    <td className="text-center">
                      {duration === 0
                        ? "Megegyezés szerint"
                        : duration.join(" - ") + " perc"}
                    </td>
                    <td className="min-w-[130px] text-center">
                      {price === 0 ? "Ingyenes" : formatPrice(price, priceUnit)}
                    </td>
                    <td>
                      {comment}{" "}
                      {commentConsultation ? " Konzultációt tartalmazza." : ""}
                    </td>
                  </tr>
                  {pass &&
                    pass.map(({ occasion, price: passPrice }) => (
                      <tr key={title + occasion}>
                        <td className="text-right">
                          {occasion} alkalmas bérlet
                        </td>
                        <td></td>
                        <td className="min-w-[130px] text-center">
                          {formatPrice(passPrice, priceUnit)}
                        </td>
                        {passIncludesFreeConsultation ? (
                          <td>
                            <span className="inline-block mr-1 px-2 bg-yellow-400 rounded-md">
                              {formatPrice(occasion * price - passPrice)}{" "}
                            </span>
                            kedvezmény + {occasion === 5 ? 1 : 2}{" "}
                            <strong>ingyenes</strong> konzultáció
                          </td>
                        ) : (
                          <td>
                            <span className="inline-block mr-1 px-2 bg-yellow-400 rounded-md">
                              {formatPrice(occasion * price - passPrice)}{" "}
                            </span>
                            kedvezmény
                          </td>
                        )}
                      </tr>
                    ))}
                </React.Fragment>
              );
            }
          )}
        </tbody>
      </table>
    </div>
  );
}
